import React, { memo, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';

import { showErrorMessageAction } from 'store/global/actions';
import { saveCallAction, TELEPHONY_ACTION_TYPE } from 'store/system/actions';
import { selectCallState } from 'store/system/selectors';

import {
  buttonRowStyles,
  containerStyles,
  fieldGroupStyles,
  headerStyles,
  labelStyles,
  saveButtonStyles,
  selectStyles,
  skipButtonStyles,
  subtitleStyles,
  textFieldStyles,
  titleStyles,
} from './PostCallForm.styles';
import { PostCallFormTestIds } from './PostCallForm.testIds';

const DISPOSITION_OPTIONS = [
  { value: 'CONNECTED', label: 'Connected' },
  { value: 'BUSY', label: 'Busy' },
  { value: 'LIVE_MESSAGE', label: 'Left live message' },
  { value: 'VOICEMAIL', label: 'Left voicemail' },
  { value: 'NO_ANSWER', label: 'No answer' },
  { value: 'WRONG_NUMBER', label: 'Wrong number' },
  { value: 'HANG_UP', label: 'Hung up' },
  { value: 'INVALID_NUMBER', label: 'Invalid number' },
];

const PostCallForm = () => {
  const dispatch = useDispatch();
  const callState = useSelector(selectCallState);
  const [disposition, setDisposition] = useState('');
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [dispositionError, setDispositionError] = useState('');
  const dispositionRef = useRef(null);

  const onDispositionChange = useCallback(e => {
    setDisposition(e.target.value);
    setDispositionError('');
  }, []);

  const onNoteChange = useCallback(e => {
    setNote(e.target.value);
  }, []);

  const onSave = useCallback(async () => {
    if (!disposition) {
      setDispositionError('Please select an outcome');
      dispositionRef.current?.focus();

      return;
    }

    setIsSaving(true);

    try {
      await dispatch(
        saveCallAction({
          dispositionType: disposition,
          note,
          telnyxCallId: callState.callId,
        }),
      );
    } catch (error) {
      dispatch(showErrorMessageAction({ message: 'Failed to save call summary', err: error }));
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, disposition, note, callState.callId]);

  const onSkip = useCallback(() => {
    dispatch({ type: TELEPHONY_ACTION_TYPE.CALL_RESET });
  }, [dispatch]);

  const displayNumber = callState.dialedNumber || callState.callerNumber || 'Unknown';

  const onSubmit = useCallback(
    e => {
      e.preventDefault();
      onSave();
    },
    [onSave],
  );

  return (
    <Box component="form" sx={containerStyles} onSubmit={onSubmit}>
      <Box sx={headerStyles}>
        <Typography sx={titleStyles} variant="bold16">
          Call Summary
        </Typography>
        <Typography sx={subtitleStyles} variant="regular12">
          {displayNumber}
        </Typography>
      </Box>

      <Box sx={fieldGroupStyles}>
        <Typography sx={labelStyles} variant="regular12">
          Outcome
        </Typography>
        <TextField
          fullWidth
          select
          data-testid={PostCallFormTestIds.OUTCOME}
          error={!!dispositionError}
          helperText={dispositionError}
          inputRef={dispositionRef}
          placeholder="Select outcome"
          size="small"
          sx={selectStyles}
          value={disposition}
          onChange={onDispositionChange}
        >
          {DISPOSITION_OPTIONS.map(option => (
            <MenuItem
              data-testid={PostCallFormTestIds.OUTCOME_OPTION}
              key={option.value}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={fieldGroupStyles}>
        <Typography sx={labelStyles} variant="regular12">
          Notes
        </Typography>
        <TextField
          fullWidth
          multiline
          data-testid={PostCallFormTestIds.NOTES}
          placeholder="Add notes about this call..."
          rows={3}
          size="small"
          sx={textFieldStyles}
          value={note}
          onChange={onNoteChange}
        />
      </Box>

      <Box sx={buttonRowStyles}>
        <Button
          data-testid={PostCallFormTestIds.SKIP}
          disabled={isSaving}
          sx={skipButtonStyles}
          variant="outlined"
          onClick={onSkip}
        >
          Skip
        </Button>
        <Button
          data-testid={PostCallFormTestIds.SAVE}
          disabled={!disposition || isSaving}
          sx={saveButtonStyles}
          type="submit"
          variant="contained"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default memo(PostCallForm);
